import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, FolderKanban, BookOpen, LogOut, Trash2, Plus, Video, Phone, Building2, Newspaper, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

const inputCls =
  "w-full rounded-xl bg-ink border border-white/10 px-3.5 py-2.5 text-sm text-[#F8F6F0] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 transition-colors";

const STATUS_STYLES = {
  new: "text-gold-pale border-gold/40 bg-gold/10",
  confirmed: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  done: "text-zinc-400 border-white/10 bg-white/5",
};

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("appointments");
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState({ downloads: [], sales: [] });
  const [projects, setProjects] = useState([]);
  const [listings, setListings] = useState([]);
  const [posts, setPosts] = useState([]);
  const [trendsJson, setTrendsJson] = useState("[]");
  const [offersJson, setOffersJson] = useState("[]");
  const [projectForm, setProjectForm] = useState({
    track: "investor", name: "", location: "", price: "", highlight: "",
    handover: "", payment_plan: "", why: "", image_url: "",
  });
  const [listingForm, setListingForm] = useState({
    title: "", community: "", type: "apartment", beds: 1, area_sqft: 800,
    price_aed: 1000000, price_label: "", status: "ready", image_url: "", description: "",
  });
  const [postForm, setPostForm] = useState({
    slug: "", title: "", excerpt: "", date: "", image_url: "", content: "",
  });
  const navigate = useNavigate();

  const loadAll = async () => {
    try {
      const [s, a, l, p, li, b, g] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/appointments"),
        api.get("/admin/leads"),
        api.get("/projects"),
        api.get("/listings"),
        api.get("/blog"),
        api.get("/guide"),
      ]);
      setStats(s.data);
      setAppointments(a.data);
      setLeads(l.data);
      setProjects(p.data);
      setListings(li.data);
      setPosts(b.data);
      setTrendsJson(JSON.stringify(g.data.trends || [], null, 2));
      setOffersJson(JSON.stringify(g.data.offers || [], null, 2));
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  };

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setMe(r.data); loadAll(); })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const run = (fn, ok, fail) => async (...args) => {
    try {
      await fn(...args);
      toast.success(ok);
    } catch {
      toast.error(fail);
    }
  };

  const setStatus = run((id, status) => api.patch(`/admin/appointments/${id}`, { status })
    .then((r) => {
      if (r.data?.zoom_join_url) toast.success("Zoom meeting created & emailed to the visitor");
      loadAll();
    }),
    "Status updated", "Could not update status");

  const addProject = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/projects", projectForm);
      toast.success("Project added");
      setProjectForm({ track: "investor", name: "", location: "", price: "", highlight: "", handover: "", payment_plan: "", why: "", image_url: "" });
      loadAll();
    } catch { toast.error("Could not add project — check all fields"); }
  };

  const deleteProject = run((id) => api.delete(`/admin/projects/${id}`)
    .then(() => setProjects((list) => list.filter((p) => p.id !== id))),
    "Project removed", "Could not delete project");

  const addListing = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...listingForm,
        beds: Number(listingForm.beds),
        area_sqft: Number(listingForm.area_sqft),
        price_aed: Number(listingForm.price_aed),
        price_label: listingForm.price_label || `AED ${Number(listingForm.price_aed).toLocaleString()}`,
      };
      await api.post("/admin/listings", payload);
      toast.success("Listing added");
      setListingForm({ title: "", community: "", type: "apartment", beds: 1, area_sqft: 800, price_aed: 1000000, price_label: "", status: "ready", image_url: "", description: "" });
      loadAll();
    } catch { toast.error("Could not add listing — check all fields"); }
  };

  const deleteListing = run((id) => api.delete(`/admin/listings/${id}`)
    .then(() => setListings((list) => list.filter((l) => l.id !== id))),
    "Listing removed", "Could not delete listing");

  const addPost = async (e) => {
    e.preventDefault();
    try {
      const content = postForm.content.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.post("/admin/blog", { ...postForm, content });
      toast.success("Article published");
      setPostForm({ slug: "", title: "", excerpt: "", date: "", image_url: "", content: "" });
      loadAll();
    } catch { toast.error("Could not publish — slug must be lowercase-letters-dashes, all fields required"); }
  };

  const deletePost = run((slug) => api.delete(`/admin/blog/${slug}`)
    .then(() => setPosts((list) => list.filter((p) => p.slug !== slug))),
    "Article removed", "Could not delete article");

  const saveGuide = async () => {
    try {
      const trends = JSON.parse(trendsJson);
      const offers = JSON.parse(offersJson);
      if (!Array.isArray(trends) || !Array.isArray(offers)) throw new Error();
      await api.put("/admin/guide", { trends, offers });
      toast.success("Guide updated");
    } catch (e) {
      if (e.response) toast.error("Could not save guide");
      else toast.error("Invalid JSON — each side must be a list of {title, text}");
    }
  };

  if (!me) {
    return <main className="min-h-screen bg-ink flex items-center justify-center text-zinc-500 text-sm">Loading…</main>;
  }

  const TABS = [
    { key: "appointments", label: "Appointments", icon: CalendarClock },
    { key: "leads", label: "Guide leads", icon: Users },
    { key: "listings", label: "Listings", icon: Building2 },
    { key: "projects", label: "Projects", icon: FolderKanban },
    { key: "blog", label: "Journal", icon: Newspaper },
    { key: "guide", label: "Guide content", icon: BookOpen },
  ];

  return (
    <main className="min-h-screen bg-ink text-[#F8F6F0]" data-testid="admin-dashboard">
      <header className="glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <a href="/" className="hidden sm:block text-xs text-zinc-500 hover:text-gold-light transition-colors">View site</a>
            <span className="hidden sm:block text-xs text-zinc-500">{me.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn"
                    className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-gold-light transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              ["Total bookings", stats.appointments_total],
              ["New requests", stats.appointments_new],
              ["Guide sales", stats.guide_sales],
              ["Guide downloads", stats.guide_leads],
            ].map(([label, value]) => (
              <div key={label} className="gold-ring rounded-2xl bg-ink-card p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
                <p className="font-serif text-3xl gold-text">{value}</p>
                <p className="text-xs text-zinc-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-8 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-medium transition-colors ${
                      tab === t.key ? "bg-gold text-ink" : "gold-ring text-zinc-300 hover:text-gold-pale"
                    }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "appointments" && (
          <div className="space-y-4" data-testid="admin-appointments-list">
            {appointments.length === 0 && <p className="text-sm text-zinc-500">No appointment requests yet.</p>}
            {appointments.map((a) => (
              <div key={a.id} className="gold-ring rounded-2xl bg-ink-card p-6 flex flex-col lg:flex-row lg:items-center gap-4"
                   data-testid={`admin-appointment-${a.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-serif text-lg">{a.name}</p>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-white/10 rounded-full px-2.5 py-0.5">
                      {a.category.replace("_", " ")}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-gold-pale">
                      {a.mode === "zoom" ? <Video size={12} /> : <Phone size={12} />}
                      {a.mode === "zoom" ? "Zoom" : "Phone"}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1.5">{a.email} · {a.phone}</p>
                  <p className="text-xs text-zinc-400 mt-1">{a.date} at {a.time} GST</p>
                  {a.message && <p className="text-sm text-zinc-300 mt-3 leading-relaxed">“{a.message}”</p>}
                  {a.zoom_join_url && (
                    <p className="text-xs mt-2 text-gold-pale" data-testid={`admin-zoom-link-${a.id}`}>
                      Zoom link sent: <a href={a.zoom_join_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{a.zoom_join_url}</a>
                    </p>
                  )}
                </div>
                <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)}
                        data-testid={`admin-appointment-status-${a.id}`}
                        className={`rounded-full border px-4 py-2 text-xs font-medium bg-transparent focus:outline-none cursor-pointer ${STATUS_STYLES[a.status]}`}>
                  <option value="new" className="bg-ink">New</option>
                  <option value="confirmed" className="bg-ink">Confirmed</option>
                  <option value="done" className="bg-ink">Done</option>
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <div data-testid="admin-leads-list">
            <p className="text-xs text-zinc-500 mb-4">Guide buyers and downloaders — follow up while they're warm.</p>
            <h3 className="font-serif text-lg text-gold-pale mb-3">Paid guide sales</h3>
            <div className="space-y-3 mb-8">
              {leads.sales.length === 0 && <p className="text-sm text-zinc-500">No sales yet.</p>}
              {leads.sales.map((s) => (
                <div key={s.id} className="gold-ring rounded-2xl bg-ink-card p-5 flex items-center justify-between gap-4"
                     data-testid={`admin-sale-${s.id}`}>
                  <div>
                    <p className="font-serif text-base">{s.name}</p>
                    <p className="text-xs text-zinc-400">{s.email} · {s.payment_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gold-pale">{s.currency} {(s.amount / 100).toFixed(0)}</p>
                    <p className="text-[11px] text-zinc-500">{new Date(s.paid_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
            <h3 className="font-serif text-lg text-gold-pale mb-3">Free downloads (earlier capture)</h3>
            <div className="space-y-3">
              {leads.downloads.length === 0 && <p className="text-sm text-zinc-500">No downloads yet.</p>}
              {leads.downloads.map((l) => (
                <div key={l.id} className="gold-ring rounded-2xl bg-ink-card p-5 flex items-center justify-between gap-4"
                     data-testid={`admin-lead-${l.id}`}>
                  <div>
                    <p className="font-serif text-base">{l.name}</p>
                    <p className="text-xs text-zinc-400">{l.email}</p>
                  </div>
                  <p className="text-[11px] text-zinc-500">{new Date(l.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "listings" && (
          <div className="grid lg:grid-cols-3 gap-8" data-testid="admin-listings-panel">
            <form onSubmit={addListing} className="gold-ring rounded-2xl bg-ink-card p-6 space-y-3 lg:sticky lg:top-28 self-start"
                  data-testid="admin-listing-form">
              <h3 className="font-serif text-lg mb-2">Add listing</h3>
              <input required placeholder="Title" value={listingForm.title} data-testid="admin-listing-title-input"
                     onChange={(e) => setListingForm({ ...listingForm, title: e.target.value })} className={inputCls} />
              <input required placeholder="Community (e.g. Dubai Marina)" value={listingForm.community} data-testid="admin-listing-community-input"
                     onChange={(e) => setListingForm({ ...listingForm, community: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select value={listingForm.type} onChange={(e) => setListingForm({ ...listingForm, type: e.target.value })}
                        className={inputCls} data-testid="admin-listing-type-select">
                  <option value="apartment">Apartment</option>
                  <option value="villa">Villa</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="penthouse">Penthouse</option>
                </select>
                <select value={listingForm.status} onChange={(e) => setListingForm({ ...listingForm, status: e.target.value })}
                        className={inputCls} data-testid="admin-listing-status-select">
                  <option value="ready">Ready</option>
                  <option value="off-plan">Off-plan</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input type="number" min="0" placeholder="Beds" value={listingForm.beds} data-testid="admin-listing-beds-input"
                       onChange={(e) => setListingForm({ ...listingForm, beds: e.target.value })} className={inputCls} />
                <input type="number" min="1" placeholder="Sqft" value={listingForm.area_sqft} data-testid="admin-listing-area-input"
                       onChange={(e) => setListingForm({ ...listingForm, area_sqft: e.target.value })} className={inputCls} />
                <input type="number" min="1" placeholder="Price AED" value={listingForm.price_aed} data-testid="admin-listing-price-input"
                       onChange={(e) => setListingForm({ ...listingForm, price_aed: e.target.value })} className={inputCls} />
              </div>
              <input placeholder="Image URL" value={listingForm.image_url} data-testid="admin-listing-image-input"
                     onChange={(e) => setListingForm({ ...listingForm, image_url: e.target.value })} className={inputCls} />
              <textarea rows={2} placeholder="Short description" value={listingForm.description} data-testid="admin-listing-description-input"
                        onChange={(e) => setListingForm({ ...listingForm, description: e.target.value })} className={`${inputCls} resize-none`} />
              <button type="submit" data-testid="admin-listing-add-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors">
                <Plus size={15} /> Add listing
              </button>
            </form>
            <div className="lg:col-span-2 space-y-3">
              {listings.map((l) => (
                <div key={l.id} className="gold-ring rounded-2xl bg-ink-card p-4 flex items-center gap-4"
                     data-testid={`admin-listing-${l.id}`}>
                  {l.image_url && <img src={l.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base truncate">{l.title}</p>
                    <p className="text-xs text-zinc-500">{l.community} · {l.price_label} · {l.status}</p>
                  </div>
                  <button onClick={() => deleteListing(l.id)} data-testid={`admin-listing-delete-${l.id}`}
                          className="text-zinc-500 hover:text-red-400 transition-colors p-2" aria-label="Delete listing">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "projects" && (
          <div className="grid lg:grid-cols-3 gap-8" data-testid="admin-projects-panel">
            <form onSubmit={addProject} className="gold-ring rounded-2xl bg-ink-card p-6 space-y-3 lg:sticky lg:top-28 self-start"
                  data-testid="admin-project-form">
              <h3 className="font-serif text-lg mb-2">Add project</h3>
              <select value={projectForm.track} onChange={(e) => setProjectForm({ ...projectForm, track: e.target.value })}
                      data-testid="admin-project-track-select" className={inputCls}>
                <option value="investor">Investor track</option>
                <option value="end_user">End-user track</option>
              </select>
              {[["name", "Project name"], ["location", "Location"], ["price", "Price (e.g. From AED 1.2M)"],
                ["highlight", "Highlight (e.g. ~9% target yield)"], ["handover", "Handover (e.g. Q4 2026)"],
                ["payment_plan", "Payment plan"], ["image_url", "Image URL"]].map(([k, ph]) => (
                <input key={k} required={["name", "location", "price"].includes(k)} data-testid={`admin-project-${k}-input`}
                       placeholder={ph} value={projectForm[k]}
                       onChange={(e) => setProjectForm({ ...projectForm, [k]: e.target.value })} className={inputCls} />
              ))}
              <textarea rows={2} placeholder="Why it matters" value={projectForm.why} data-testid="admin-project-why-input"
                        onChange={(e) => setProjectForm({ ...projectForm, why: e.target.value })} className={`${inputCls} resize-none`} />
              <button type="submit" data-testid="admin-project-add-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors">
                <Plus size={15} /> Add project
              </button>
            </form>
            <div className="lg:col-span-2 space-y-3">
              {projects.map((p) => (
                <div key={p.id} className="gold-ring rounded-2xl bg-ink-card p-4 flex items-center gap-4"
                     data-testid={`admin-project-${p.id}`}>
                  {p.image_url && <img src={p.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base truncate">{p.name}</p>
                    <p className="text-xs text-zinc-500">{p.location} · {p.price} · {p.track === "investor" ? "Investor" : "End-user"}</p>
                  </div>
                  <button onClick={() => deleteProject(p.id)} data-testid={`admin-project-delete-${p.id}`}
                          className="text-zinc-500 hover:text-red-400 transition-colors p-2" aria-label="Delete project">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "blog" && (
          <div className="grid lg:grid-cols-3 gap-8" data-testid="admin-blog-panel">
            <form onSubmit={addPost} className="gold-ring rounded-2xl bg-ink-card p-6 space-y-3 lg:sticky lg:top-28 self-start"
                  data-testid="admin-post-form">
              <h3 className="font-serif text-lg mb-2">New article</h3>
              <input required placeholder="slug-like-this" value={postForm.slug} data-testid="admin-post-slug-input"
                     onChange={(e) => setPostForm({ ...postForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} className={inputCls} />
              <input required placeholder="Title" value={postForm.title} data-testid="admin-post-title-input"
                     onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} className={inputCls} />
              <input required placeholder="Excerpt (one line)" value={postForm.excerpt} data-testid="admin-post-excerpt-input"
                     onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Date (e.g. July 2026)" value={postForm.date} data-testid="admin-post-date-input"
                       onChange={(e) => setPostForm({ ...postForm, date: e.target.value })} className={inputCls} />
                <input placeholder="Image URL" value={postForm.image_url} data-testid="admin-post-image-input"
                       onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })} className={inputCls} />
              </div>
              <textarea rows={6} placeholder={"Body — one paragraph per line.\nStart a line with ## for a heading."} value={postForm.content}
                        data-testid="admin-post-content-input"
                        onChange={(e) => setPostForm({ ...postForm, content: e.target.value })} className={`${inputCls} resize-y`} />
              <button type="submit" data-testid="admin-post-add-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors">
                <Plus size={15} /> Publish article
              </button>
            </form>
            <div className="lg:col-span-2 space-y-3">
              {posts.map((p) => (
                <div key={p.slug} className="gold-ring rounded-2xl bg-ink-card p-4 flex items-center gap-4"
                     data-testid={`admin-post-${p.slug}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base truncate">{p.title}</p>
                    <p className="text-xs text-zinc-500">/blog/{p.slug} · {p.date}</p>
                  </div>
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-gold-light hover:text-gold-pale transition-colors">View</a>
                  <button onClick={() => deletePost(p.slug)} data-testid={`admin-post-delete-${p.slug}`}
                          className="text-zinc-500 hover:text-red-400 transition-colors p-2" aria-label="Delete article">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "guide" && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-guide-panel">
            {[["Market trends", trendsJson, setTrendsJson, "admin-trends-editor"],
              ["Attractive offers", offersJson, setOffersJson, "admin-offers-editor"]].map(([label, val, setter, tid]) => (
              <div key={label} className="gold-ring rounded-2xl bg-ink-card p-6">
                <h3 className="font-serif text-lg mb-1">{label}</h3>
                <p className="text-[11px] text-zinc-500 mb-4">JSON list of {"{ \"title\": \"…\", \"text\": \"…\" }"}</p>
                <textarea rows={12} value={val} onChange={(e) => setter(e.target.value)} data-testid={tid}
                          className={`${inputCls} font-mono text-xs leading-relaxed`} />
              </div>
            ))}
            <div className="lg:col-span-2">
              <button onClick={saveGuide} data-testid="admin-guide-save-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors">
                Save guide content
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
